import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { api } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Loader2, Sparkles, ShieldCheck, Globe2, BrainCircuit, Search, ArrowRight, Zap, AlertCircle, AlertTriangle, LogIn, LogOut, User, CheckCircle, Star, Rocket, Target, Trophy, Heart, TrendingUp, Users, Building2, Briefcase, ChevronDown, ChevronUp, FileText, Clock, DollarSign, MessageSquare, Quote, BarChart3, Menu, X, Crown, GitCompare } from "lucide-react";
import { toast } from "sonner";
import { ReportCarousel } from '../components/ReportPreview';
import Footer from '../components/Footer';
import ElegantLoader from '../components/ElegantLoader';
import PricingSection from '../components/PricingSection';
import CaseStudiesSection from '../components/CaseStudiesSection';
import MismatchWarningModal, { detectMismatches } from '../components/MismatchWarningModal';
import EvaluationModeSelector from '../components/EvaluationModeSelector';
import MultiBrandInput from '../components/MultiBrandInput';
import BrandWizard from '../components/BrandWizard';

// Dynamic Cycling "Trusted By" Component
const TrustedByCycler = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  
  const trustedBy = [
    { text: "Brand Consultants", icon: Users, color: "text-violet-600", bg: "bg-violet-100" },
    { text: "Startup Founders", icon: Rocket, color: "text-fuchsia-600", bg: "bg-fuchsia-100" },
    { text: "Consulting Firms", icon: Building2, color: "text-orange-500", bg: "bg-orange-100" },
    { text: "Marketing Agencies", icon: Briefcase, color: "text-emerald-600", bg: "bg-emerald-100" },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % trustedBy.length);
        setIsAnimating(false);
      }, 300);
    }, 2500);
    return () => clearInterval(interval);
  }, [trustedBy.length]);

  const current = trustedBy[currentIndex];
  const Icon = current.icon;

  return (
    <div className="flex flex-col items-center gap-1.5 mt-5">
      <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">Trusted By</p>
      <div className="relative h-9 flex items-center justify-center min-w-[200px]">
        <div 
          className={`flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-50 border border-slate-200 transition-all duration-300 ${isAnimating ? 'opacity-0 transform translate-y-2' : 'opacity-100 transform translate-y-0'}`}
        >
          <div className={`w-6 h-6 rounded-full ${current.bg} flex items-center justify-center`}>
            <Icon className={`w-3.5 h-3.5 ${current.color}`} />
          </div>
          <span className="font-bold text-slate-700 text-sm whitespace-nowrap">{current.text}</span>
        </div>
      </div>
      {/* Progress dots */}
      <div className="flex gap-1 mt-0.5">
        {trustedBy.map((_, idx) => (
          <div 
            key={idx} 
            className={`w-1 h-1 rounded-full transition-all duration-300 ${idx === currentIndex ? 'bg-violet-500 w-3' : 'bg-slate-300'}`}
          />
        ))}
      </div>
    </div>
  );
};

// Animated floating badge component
const FloatingBadge = ({ children, className, delay = 0 }) => (
  <div 
    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border-2 shadow-lg font-bold text-sm ${className}`}
    style={{ 
      animation: `float 3s ease-in-out infinite`,
      animationDelay: `${delay}s`
    }}
  >
    {children}
  </div>
);

// Trust badge pill
const TrustPill = ({ icon: Icon, text, color }) => (
  <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${color} font-bold text-sm shadow-md hover:scale-105 transition-transform cursor-default`}>
    <Icon className="w-4 h-4" />
    <span>{text}</span>
  </div>
);

const FeatureCard = ({ icon: Icon, title, description, color, emoji }) => (
  <div className={`p-6 rounded-3xl bg-white border-2 border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:translate-y-[-8px] hover:border-violet-200 group relative overflow-hidden`}>
    <div className="absolute top-4 right-4 text-4xl opacity-20 group-hover:opacity-40 transition-opacity">{emoji}</div>
    <div className={`w-14 h-14 rounded-2xl ${color} flex items-center justify-center mb-5 group-hover:scale-110 group-hover:rotate-3 transition-all shadow-lg`}>
      <Icon className="w-7 h-7 text-white" />
    </div>
    <h3 className="font-black text-lg text-slate-900 mb-2">{title}</h3>
    <p className="text-sm text-slate-500 font-medium leading-relaxed">{description}</p>
  </div>
);

// FAQ Accordion Item
const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border-2 border-slate-200 rounded-2xl overflow-hidden hover:border-violet-300 transition-colors">
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-slate-50 transition-colors"
    >
      <span className="font-bold text-slate-900 pr-4">{question}</span>
      {isOpen ? (
        <ChevronUp className="w-5 h-5 text-violet-600 flex-shrink-0" />
      ) : (
        <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
      )}
    </button>
    {isOpen && (
      <div className="px-6 pb-6 bg-slate-50">
        <p className="text-slate-600 leading-relaxed">{answer}</p>
      </div>
    )}
  </div>
);

// How It Works Step
const HowItWorksStep = ({ number, title, description, icon: Icon, color }) => (
  <div className="relative flex flex-col items-center text-center p-6">
    <div className={`w-16 h-16 ${color} rounded-2xl flex items-center justify-center mb-4 shadow-lg`}>
      <Icon className="w-8 h-8 text-white" />
    </div>
    <div className="absolute -top-2 -left-2 w-8 h-8 bg-slate-900 text-white rounded-full flex items-center justify-center font-black text-sm">
      {number}
    </div>
    <h3 className="font-black text-lg text-slate-900 mb-2">{title}</h3>
    <p className="text-slate-500 text-sm">{description}</p>
  </div>
);

// Testimonial Card
const TestimonialCard = ({ quote, author, role, company, avatar }) => (
  <Card className="border-2 border-slate-200 hover:border-violet-300 transition-all duration-300 hover:shadow-xl">
    <CardContent className="p-6">
      <Quote className="w-8 h-8 text-violet-300 mb-4" />
      <p className="text-slate-700 mb-6 leading-relaxed italic">&ldquo;{quote}&rdquo;</p>
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white font-bold text-lg">
          {avatar}
        </div>
        <div>
          <p className="font-bold text-slate-900">{author}</p>
          <p className="text-sm text-slate-500">{role}, {company}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Use Case Card
const UseCaseCard = ({ icon: Icon, title, description, color }) => (
  <div className="p-6 rounded-2xl bg-white border-2 border-slate-200 hover:border-violet-300 transition-all duration-300 hover:shadow-lg">
    <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center mb-4`}>
      <Icon className="w-6 h-6 text-white" />
    </div>
    <h3 className="font-bold text-slate-900 mb-2">{title}</h3>
    <p className="text-slate-500 text-sm">{description}</p>
  </div>
);

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, loginWithGoogle, logout, openAuthModal } = useAuth();
  const [loading, setLoading] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // 🧙 WIZARD MODE - Primary input method (replaces old form)
  // ═══════════════════════════════════════════════════════════════════════════
  const [showWizard, setShowWizard] = useState(false);
  
  // Loading progress state for elegant loader
  const [loadingProgress, setLoadingProgress] = useState({
    progress: 0,
    currentStep: 'starting',
    currentStepLabel: 'Initializing...',
    completedSteps: [],
    etaSeconds: 90
  });
  
  // Mismatch Warning Modal state (kept for wizard flow)
  const [showMismatchModal, setShowMismatchModal] = useState(false);
  const [detectedMismatches, setDetectedMismatches] = useState([]);
  const [pendingSubmit, setPendingSubmit] = useState(false);
  
  // Wizard payload for submission
  const [wizardPayload, setWizardPayload] = useState(null);

  // Industry options
  // ═══════════════════════════════════════════════════════════════════════════
  // ACTUAL SUBMIT - Called from wizard completion
  // ═══════════════════════════════════════════════════════════════════════════
  const executeSubmit = async (payload) => {
    setLoading(true);
    
    // Reset loading progress for elegant loader
    setLoadingProgress({
      progress: 5,
      currentStep: 'starting',
      currentStepLabel: 'Initializing analysis...',
      completedSteps: [],
      etaSeconds: payload.evaluation_mode === 'compare' ? 150 : 90
    });
    
    try {
      console.log('[LandingPage] Sending wizard payload:', payload);

      // Use async evaluation with progress callback for elegant loader
      const result = await api.evaluate(payload, (progressData) => {
        // Update elegant loader state
        setLoadingProgress({
          progress: progressData.progress || 0,
          currentStep: progressData.currentStep || 'processing',
          currentStepLabel: progressData.currentStepLabel || 'Analyzing...',
          completedSteps: progressData.completedSteps || [],
          etaSeconds: progressData.etaSeconds || 60
        });
      });
      
      console.log('[LandingPage] Received result:', result);
      
      toast.success("Analysis complete! 🎉");
      navigate('/dashboard', { 
        state: { 
          data: result, 
          query: payload,
          evaluationMode: payload.evaluation_mode // Pass mode to dashboard
        } 
      });
    } catch (error) {
      console.error('[LandingPage] Error:', error);
      
      let errorMsg = "Evaluation failed. Please try again.";
      
      // Handle timeout errors
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMsg = "Request timed out. The analysis is taking longer than expected. Please try again.";
      } else if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          errorMsg = detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'object') {
          errorMsg = detail.msg || detail.message || JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast.error(
        <div className="flex flex-col gap-1">
            <span className="font-bold">Analysis Failed</span>
            <span className="text-xs">{errorMsg}</span>
        </div>,
        { duration: 5000 }
      );
    } finally {
      setLoading(false);
      setShowWizard(false);
      setPendingSubmit(false);
    }
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // WIZARD COMPLETE HANDLER - Called when wizard finishes
  // ═══════════════════════════════════════════════════════════════════════════
  const handleWizardComplete = async (payload) => {
    console.log('[LandingPage] Wizard completed with payload:', payload);
    setWizardPayload(payload);
    
    // Check for mismatches BEFORE starting analysis
    const checkData = {
      brand_names: payload.brand_names?.join(', ') || '',
      industry: payload.industry,
      category: payload.category,
      usp: payload.usp,
      name_origin: payload.name_origin
    };
    
    const mismatches = detectMismatches(checkData);
    
    if (mismatches.length > 0) {
      // Show warning modal - let user decide
      setDetectedMismatches(mismatches);
      setShowMismatchModal(true);
      setPendingSubmit(true);
      console.log('[LandingPage] Mismatches detected:', mismatches);
      return;
    }
    
    // No mismatches - proceed directly
    executeSubmit(payload);
  };
  
  // Handle user confirming they want to continue despite mismatches
  const handleMismatchContinue = () => {
    console.log('[LandingPage] User confirmed mismatches - proceeding with analysis');
    setShowMismatchModal(false);
    setTimeout(() => {
      setDetectedMismatches([]);
    }, 100);
    if (wizardPayload) {
      executeSubmit(wizardPayload);
    }
  };
  
  // Handle user wanting to edit their inputs
  const handleMismatchEdit = () => {
    console.log('[LandingPage] User chose to edit inputs');
    setShowMismatchModal(false);
    setPendingSubmit(false);
    setTimeout(() => {
      setDetectedMismatches([]);
    }, 100);
    // Go back to wizard
    setShowWizard(true);
    toast.info("Please review and update your inputs", { duration: 3000 });
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 🧙 WIZARD HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════
  
  const handleWizardStartProcessing = (stage, data) => {
    console.log(`[Wizard] Background processing started: ${stage}`, data);
  };

  // Get first brand name for the loader (from wizard payload if available)
  const firstBrandName = wizardPayload?.brand_names?.[0] || 'your brand';

  // ═══════════════════════════════════════════════════════════════════════════
  // 🧙 RENDER WIZARD IF ACTIVE
  // ═══════════════════════════════════════════════════════════════════════════
  if (showWizard) {
    return (
      <>
        {loading && (
          <ElegantLoader
            brandName={firstBrandName}
            progress={loadingProgress.progress}
            currentStep={loadingProgress.currentStep}
            completedSteps={loadingProgress.completedSteps}
            etaSeconds={loadingProgress.etaSeconds}
          />
        )}
        <BrandWizard 
          onComplete={handleWizardComplete}
          onStartProcessing={handleWizardStartProcessing}
        />
      </>
    );
  }

  return (
    <>
      {/* Elegant Loading Overlay */}
      {loading && (
        <ElegantLoader
          brandName={firstBrandName}
          progress={loadingProgress.progress}
          currentStep={loadingProgress.currentStep}
          completedSteps={loadingProgress.completedSteps}
          etaSeconds={loadingProgress.etaSeconds}
        />
      )}
      
      <Helmet>
        <title>RIGHTNAME | AI-Powered Brand Name Evaluation & Trademark Check</title>
        <meta name="description" content="Evaluate brand names instantly with AI. Check trademark conflicts, domain availability, social handles & get a consulting-grade RightName Score report in 90 seconds. Starting at $29 per report." />
        <link rel="canonical" href="https://rightname.ai" />
      </Helmet>
      
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 font-sans selection:bg-violet-200 overflow-x-hidden">
      {/* Add floating animation keyframes */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes pulse-ring {
          0% { transform: scale(0.8); opacity: 1; }
          100% { transform: scale(2); opacity: 0; }
        }
        @keyframes gradient-x {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes fadeInUp {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-20%] right-[-10%] w-[600px] h-[600px] bg-gradient-to-br from-violet-300/30 to-fuchsia-300/30 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] bg-gradient-to-br from-cyan-300/30 to-blue-300/30 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-[40%] left-[20%] w-[300px] h-[300px] bg-gradient-to-br from-amber-200/20 to-orange-200/20 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '2s' }} />
        
        {/* Floating decorative elements */}
        <div className="absolute top-[15%] left-[10%] text-6xl opacity-10 animate-bounce" style={{ animationDuration: '3s' }}>✨</div>
        <div className="absolute top-[25%] right-[15%] text-5xl opacity-10 animate-bounce" style={{ animationDuration: '4s', animationDelay: '1s' }}>🚀</div>
        <div className="absolute bottom-[30%] left-[5%] text-4xl opacity-10 animate-bounce" style={{ animationDuration: '3.5s', animationDelay: '0.5s' }}>💎</div>
        <div className="absolute bottom-[20%] right-[10%] text-5xl opacity-10 animate-bounce" style={{ animationDuration: '4.5s', animationDelay: '1.5s' }}>⚡</div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 py-8 lg:py-12">
        
        {/* Header with Navigation */}
        <div className="flex justify-between items-center mb-12 lg:mb-16">
            <div className="flex items-center gap-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_name-radar-1/artifacts/qrfrrizq_R.png" 
                  alt="RIGHTNAME Logo" 
                  className="w-10 h-10 md:w-12 md:h-12 rounded-2xl shadow-xl shadow-violet-300/50 hover:scale-110 transition-transform cursor-pointer"
                />
                <h1 className="text-xl md:text-2xl lg:text-3xl font-black bg-clip-text text-transparent bg-gradient-to-r from-violet-600 via-fuchsia-500 to-orange-500 tracking-tight">RIGHTNAME</h1>
            </div>
            
            {/* Desktop Navigation Links */}
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/" className="text-sm font-semibold text-violet-600">Home</Link>
              <a href="#features" className="text-sm font-semibold text-slate-600 hover:text-violet-600 transition-colors">Features</a>
              
              {/* Tools Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-violet-600 transition-colors outline-none">
                  Tools
                  <ChevronDown className="w-4 h-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuItem asChild>
                    <Link to="/" className="flex items-center gap-3 cursor-pointer">
                      <div className="p-1.5 bg-violet-100 rounded-lg">
                        <Sparkles className="w-4 h-4 text-violet-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900">Brand Evaluation</div>
                        <div className="text-xs text-slate-500">For New Brands</div>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/brand-audit" className="flex items-center gap-3 cursor-pointer">
                      <div className="p-1.5 bg-emerald-100 rounded-lg">
                        <BarChart3 className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900">Brand Audit</div>
                        <div className="text-xs text-slate-500">For Existing Brands</div>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              <a href="#how-it-works" className="text-sm font-semibold text-slate-600 hover:text-violet-600 transition-colors">How It Works</a>
              <Link to="/blog" className="text-sm font-semibold text-slate-600 hover:text-violet-600 transition-colors">Blog</Link>
              <a href="#faq" className="text-sm font-semibold text-slate-600 hover:text-violet-600 transition-colors">FAQ</a>
            </nav>
            
            <div className="flex items-center gap-2 md:gap-3">
                {/* Mobile Menu Button */}
                <button 
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="md:hidden p-2 rounded-xl bg-white border-2 border-slate-200 shadow-sm hover:bg-slate-50 transition-colors"
                >
                  {mobileMenuOpen ? <X className="w-5 h-5 text-slate-600" /> : <Menu className="w-5 h-5 text-slate-600" />}
                </button>
                
                {authLoading ? (
                    <div className="w-10 h-10 rounded-full bg-slate-100 animate-pulse" />
                ) : user ? (
                    <div className="flex items-center gap-2 md:gap-3">
                        <Link 
                          to="/my-reports" 
                          className="flex items-center gap-2 px-3 md:px-4 py-2 bg-violet-50 hover:bg-violet-100 border-2 border-violet-200 rounded-full shadow-sm transition-colors"
                        >
                          <FileText className="w-4 h-4 text-violet-600" />
                          <span className="text-sm font-bold text-violet-700 hidden sm:inline">My Reports</span>
                        </Link>
                        <div className="flex items-center gap-2 px-3 md:px-4 py-2 bg-white border-2 border-slate-200 rounded-full shadow-sm">
                            {user.picture ? (
                                <img src={user.picture} alt={user.name} className="w-6 h-6 md:w-7 md:h-7 rounded-full ring-2 ring-violet-200" />
                            ) : (
                                <User className="w-5 h-5 text-slate-500" />
                            )}
                            <span className="text-sm font-bold text-slate-700 hidden sm:inline">{user.name?.split(' ')[0]}</span>
                        </div>
                        <Button variant="outline" size="sm" onClick={logout} className="text-slate-600 rounded-full border-2 hidden sm:flex">
                            <LogOut className="w-4 h-4 mr-1" />
                            <span className="hidden sm:inline">Sign Out</span>
                        </Button>
                    </div>
                ) : (
                    <Button onClick={() => openAuthModal()} className="bg-gradient-to-r from-violet-600 via-fuchsia-500 to-orange-500 hover:from-violet-700 hover:via-fuchsia-600 hover:to-orange-600 text-white font-bold shadow-xl shadow-violet-300/50 rounded-full px-4 md:px-6 text-sm hover:scale-105 transition-transform">
                        <LogIn className="w-4 h-4 mr-1 md:mr-2" />
                        <span className="hidden sm:inline">Sign In</span>
                        <span className="sm:hidden">Login</span>
                    </Button>
                )}
            </div>
        </div>
        
        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setMobileMenuOpen(false)}>
            <div 
              className="absolute right-0 top-0 h-full w-72 bg-white shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-8">
                  <h2 className="text-lg font-bold text-slate-900">Menu</h2>
                  <button 
                    onClick={() => setMobileMenuOpen(false)}
                    className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
                  >
                    <X className="w-5 h-5 text-slate-600" />
                  </button>
                </div>
                
                <nav className="space-y-2">
                  <Link 
                    to="/" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-violet-600 bg-violet-50 font-semibold"
                  >
                    <Sparkles className="w-5 h-5" />
                    Home
                  </Link>
                  
                  <a 
                    href="#features" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-50 font-semibold transition-colors"
                  >
                    <Star className="w-5 h-5" />
                    Features
                  </a>
                  
                  <div className="pt-2 pb-1">
                    <p className="px-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Tools</p>
                  </div>
                  
                  <Link 
                    to="/" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-50 transition-colors"
                  >
                    <div className="p-1.5 bg-violet-100 rounded-lg">
                      <Sparkles className="w-4 h-4 text-violet-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">Brand Evaluation</div>
                      <div className="text-xs text-slate-500">For New Brands</div>
                    </div>
                  </Link>
                  
                  <Link 
                    to="/brand-audit" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-50 transition-colors"
                  >
                    <div className="p-1.5 bg-emerald-100 rounded-lg">
                      <BarChart3 className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">Brand Audit</div>
                      <div className="text-xs text-slate-500">For Existing Brands</div>
                    </div>
                  </Link>
                  
                  <div className="pt-4 border-t border-slate-100 mt-4">
                    <a 
                      href="#how-it-works" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-50 font-semibold transition-colors"
                    >
                      <Target className="w-5 h-5" />
                      How It Works
                    </a>
                    
                    <Link 
                      to="/blog" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-50 font-semibold transition-colors"
                    >
                      <FileText className="w-5 h-5" />
                      Blog
                    </Link>
                    
                    <a 
                      href="#faq" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-50 font-semibold transition-colors"
                    >
                      <MessageSquare className="w-5 h-5" />
                      FAQ
                    </a>
                  </div>
                  
                  {/* My Reports link for logged in users */}
                  {user && (
                    <div className="pt-4 border-t border-slate-100 mt-4">
                      <Link 
                        to="/my-reports"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 rounded-xl text-violet-600 bg-violet-50 font-semibold transition-colors"
                      >
                        <FileText className="w-5 h-5" />
                        My Reports
                      </Link>
                    </div>
                  )}
                  
                  {/* Mobile Sign Out Button for logged in users */}
                  {user && (
                    <div className="pt-4 border-t border-slate-100 mt-4">
                      <button 
                        onClick={() => { logout(); setMobileMenuOpen(false); }}
                        className="flex items-center gap-3 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 font-semibold transition-colors w-full"
                      >
                        <LogOut className="w-5 h-5" />
                        Sign Out
                      </button>
                    </div>
                  )}
                </nav>
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            
            {/* Left Content: Hero Text */}
            <div className="space-y-8 relative z-10">
                <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border-2 border-violet-200 shadow-lg text-sm font-black text-violet-700">
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                    </span>
                    🤖 AI-Powered Brand Intelligence
                </div>
                
                <h1 className="text-5xl lg:text-6xl font-black text-slate-900 leading-[1.1] tracking-tight">
                    Don't Fall in Love <br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 via-fuchsia-500 to-orange-500">
                        With a Name
                    </span>
                    <br /> 
                    <span className="inline-flex items-center">
                      Until It Clears Trademark
                      <span className="ml-3 text-4xl">⚖️</span>
                    </span>
                </h1>
                
                <p className="text-xl text-slate-600 font-medium leading-relaxed max-w-lg">
                    Get instant, <span className="text-violet-600 font-bold">consulting-grade</span> <span className="text-slate-900 font-black">BRAND NAME ANALYSIS</span> on trademark risk, cultural resonance, and domain availability. <span className="text-fuchsia-600 font-bold">No guesswork.</span>
                </p>

                {/* Quick Stats - Honest & Meaningful */}
                <div className="flex items-center gap-6 pt-2">
                    <div className="text-center">
                        <div className="text-3xl font-black text-violet-600">6</div>
                        <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Data Sources</div>
                    </div>
                    <div className="w-px h-12 bg-slate-200"></div>
                    <div className="text-center">
                        <div className="text-3xl font-black text-fuchsia-600">45+</div>
                        <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">NICE Classes</div>
                    </div>
                    <div className="w-px h-12 bg-slate-200"></div>
                    <div className="text-center">
                        <div className="text-3xl font-black text-orange-500">Real-Time</div>
                        <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Trademark Search</div>
                    </div>
                </div>

                {/* Feature Pills */}
                <div className="flex flex-wrap gap-3 pt-4">
                    <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border border-emerald-200">
                        <ShieldCheck className="w-4 h-4 text-emerald-600" />
                        <span className="font-bold text-sm text-emerald-700">Legal Risk Check</span>
                    </div>
                    <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-200">
                        <Globe2 className="w-4 h-4 text-blue-600" />
                        <span className="font-bold text-sm text-blue-700">Global Culture Fit</span>
                    </div>
                    <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-violet-50 border border-violet-200">
                        <BrainCircuit className="w-4 h-4 text-violet-600" />
                        <span className="font-bold text-sm text-violet-700">AI Perception</span>
                    </div>
                    <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-orange-50 border border-orange-200">
                        <Search className="w-4 h-4 text-orange-600" />
                        <span className="font-bold text-sm text-orange-700">Domain Scout</span>
                    </div>
                </div>

                {/* Product Hunt Badge */}
                <div className="pt-6">
                    <a 
                        href="https://www.producthunt.com/products/rightname?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-rightname" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-block hover:opacity-90 transition-opacity"
                    >
                        <img 
                            src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1066204&theme=light&t=1769075215856" 
                            alt="RIGHTNAME - Serious brand checks before you fall in love with a name | Product Hunt" 
                            width="250" 
                            height="54"
                            className="rounded-lg"
                        />
                    </a>
                </div>
            </div>

            {/* Right Content: Start Analysis CTA Card */}
            <div className="relative z-10">
                <div className="absolute -inset-2 bg-gradient-to-r from-violet-600 via-fuchsia-500 to-orange-500 rounded-[2rem] blur opacity-30 group-hover:opacity-40 transition-opacity"></div>
                <Card className="border-2 border-white/50 shadow-2xl rounded-[2rem] overflow-hidden bg-white/90 backdrop-blur-xl relative">
                    <CardContent className="p-8">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-black text-slate-900 flex items-center gap-2">
                                <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center">
                                    <Zap className="w-5 h-5 text-white" />
                                </div>
                                Start Analysis
                            </h3>
                            <div className="flex gap-2">
                                <div className="w-3 h-3 rounded-full bg-red-400 shadow-sm"></div>
                                <div className="w-3 h-3 rounded-full bg-amber-400 shadow-sm"></div>
                                <div className="w-3 h-3 rounded-full bg-green-400 shadow-sm"></div>
                            </div>
                        </div>

                        {/* Main CTA - Launch Wizard */}
                        <div className="space-y-6">
                            <div className="text-center py-4">
                                <p className="text-slate-600 mb-2">Get your comprehensive brand report in</p>
                                <div className="text-5xl font-black bg-gradient-to-r from-violet-600 via-fuchsia-600 to-orange-500 bg-clip-text text-transparent">
                                    ~90 seconds
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={() => setShowWizard(true)}
                                data-testid="start-wizard-btn"
                                className="w-full p-5 bg-gradient-to-r from-violet-600 via-fuchsia-600 to-orange-500 rounded-xl text-white font-bold flex items-center justify-center gap-3 hover:from-violet-700 hover:via-fuchsia-700 hover:to-orange-600 transition-all shadow-lg shadow-violet-500/30 hover:shadow-xl hover:scale-[1.02] text-lg"
                            >
                                <Sparkles className="w-6 h-6" />
                                Start Brand Analysis
                                <ArrowRight className="w-6 h-6" />
                            </button>

                            {/* Features List */}
                            <div className="grid grid-cols-2 gap-3 pt-4">
                                <div className="flex items-center gap-2 text-sm text-slate-600">
                                    <ShieldCheck className="w-4 h-4 text-emerald-500" />
                                    <span>Trademark Check</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-slate-600">
                                    <Globe2 className="w-4 h-4 text-blue-500" />
                                    <span>Cultural Analysis</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-slate-600">
                                    <BrainCircuit className="w-4 h-4 text-violet-500" />
                                    <span>AI Perception</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-slate-600">
                                    <Search className="w-4 h-4 text-orange-500" />
                                    <span>Domain Scout</span>
                                </div>
                            </div>

                            {/* Compare Mode Hint */}
                            <div className="text-center pt-2 border-t border-slate-100">
                                <p className="text-xs text-slate-400">
                                    Compare up to 5 brand names • Single deep analysis
                                </p>
                            </div>

                            {/* Trusted By */}
                            <TrustedByCycler />
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>

 

        {/* Report Preview Carousel */}
        <ReportCarousel />

        {/* Feature Grid Section */}
        <div id="features" className="mt-28">
            <div className="text-center mb-16">
                <Badge className="mb-4 bg-violet-100 text-violet-700 border-violet-200 px-4 py-1.5 text-sm font-black">
                  💡 What We Analyze
                </Badge>
                <h2 className="text-4xl font-black text-slate-900 mb-4">
                  Everything a <span className="text-violet-600">$50k consultant</span> does.
                  <br />
                  <span className="text-fuchsia-600">In 30 seconds.</span>
                </h2>
                <p className="text-slate-500 font-medium max-w-2xl mx-auto text-lg">
                    Our AI replicates the complete workflow of premium brand consultancies.
                </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                <FeatureCard 
                    icon={BrainCircuit}
                    title="Perception Mapping"
                    description="Map your name against 6 core brand dimensions including distinctiveness and trust."
                    color="bg-gradient-to-br from-violet-500 to-violet-600"
                    emoji="🧠"
                />
                <FeatureCard 
                    icon={ShieldCheck}
                    title="Legal Sensitivity"
                    description="Probabilistic risk assessment for trademark conflicts across global registries."
                    color="bg-gradient-to-br from-emerald-500 to-emerald-600"
                    emoji="⚖️"
                />
                <FeatureCard 
                    icon={Globe2}
                    title="Cultural Check"
                    description="Linguistic safety checks in 10+ languages to prevent embarrassing fails."
                    color="bg-gradient-to-br from-fuchsia-500 to-fuchsia-600"
                    emoji="🌍"
                />
                <FeatureCard 
                    icon={Search}
                    title="Domain Scout"
                    description="Instant availability for .com and strategic alternatives based on your industry."
                    color="bg-gradient-to-br from-orange-500 to-orange-600"
                    emoji="🔍"
                />
            </div>
        </div>

        {/* How It Works Section */}
        <div id="how-it-works" className="mt-28">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-emerald-100 text-emerald-700 border-emerald-200 px-4 py-1.5 text-sm font-black">
              🚀 How It Works
            </Badge>
            <h2 className="text-4xl font-black text-slate-900 mb-4">
              Three Steps to{' '}
              <span className="text-emerald-600">Brand Clarity</span>
            </h2>
            <p className="text-slate-500 font-medium max-w-2xl mx-auto text-lg">
              From idea to actionable insights in under 90 seconds.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connector Line */}
            <div className="hidden md:block absolute top-8 left-1/4 right-1/4 h-1 bg-gradient-to-r from-violet-300 via-fuchsia-300 to-orange-300 rounded-full" />
            
            <HowItWorksStep
              number="1"
              icon={FileText}
              title="Enter Brand Names"
              description="Add up to 3 brand name options you're considering. Include your industry and target market for context."
              color="bg-gradient-to-br from-violet-500 to-violet-600"
            />
            <HowItWorksStep
              number="2"
              icon={BrainCircuit}
              title="AI Analysis"
              description="Our AI checks trademark databases, domain availability, social handles, and cultural implications in real-time."
              color="bg-gradient-to-br from-fuchsia-500 to-fuchsia-600"
            />
            <HowItWorksStep
              number="3"
              icon={Target}
              title="Get Your Report"
              description="Receive a consulting-grade RightName Score report with actionable recommendations and risk assessment."
              color="bg-gradient-to-br from-orange-500 to-orange-600"
            />
          </div>
        </div>

        {/* Use Cases Section */}
        <div className="mt-28">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-blue-100 text-blue-700 border-blue-200 px-4 py-1.5 text-sm font-black">
              👥 Who It Is For
            </Badge>
            <h2 className="text-4xl font-black text-slate-900 mb-4">
              Built for{' '}
              <span className="text-blue-600">Brand Builders</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <UseCaseCard
              icon={Rocket}
              title="Startup Founders"
              description="Validate your brand before investing in design, marketing, and legal registration."
              color="bg-gradient-to-br from-violet-500 to-violet-600"
            />
            <UseCaseCard
              icon={Users}
              title="Brand Consultants"
              description="Speed up client projects with AI-powered preliminary analysis and professional reports."
              color="bg-gradient-to-br from-blue-500 to-blue-600"
            />
            <UseCaseCard
              icon={Building2}
              title="Marketing Agencies"
              description="Add brand validation as a premium service. Generate unlimited reports for clients."
              color="bg-gradient-to-br from-emerald-500 to-emerald-600"
            />
            <UseCaseCard
              icon={Briefcase}
              title="Enterprise Teams"
              description="Standardize brand evaluation across product launches and market expansions."
              color="bg-gradient-to-br from-orange-500 to-orange-600"
            />
          </div>
        </div>

        {/* Testimonials Section */}
        <div className="mt-28">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200 px-4 py-1.5 text-sm font-black">
              ⭐ Success Stories
            </Badge>
            <h2 className="text-4xl font-black text-slate-900 mb-4">
              Loved by{' '}
              <span className="text-fuchsia-600">500+ Founders</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <TestimonialCard
              quote="RIGHTNAME saved us from a $50k trademark dispute. The AI caught a conflict our lawyers missed initially."
              author="Sarah Chen"
              role="Founder"
              company="TechFlow AI"
              avatar="SC"
            />
            <TestimonialCard
              quote="We use RIGHTNAME for every client project now. The reports are more comprehensive than what we used to deliver manually."
              author="Marcus Johnson"
              role="Brand Director"
              company="Spark Agency"
              avatar="MJ"
            />
            <TestimonialCard
              quote="The cultural analysis feature is incredible. It flagged issues with our name in 3 markets we hadn't considered."
              author="Priya Sharma"
              role="CEO"
              company="GlobalEats"
              avatar="PS"
            />
          </div>
        </div>

        {/* FAQ Section */}
        <div id="faq" className="mt-28">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-orange-100 text-orange-700 border-orange-200 px-4 py-1.5 text-sm font-black">
              ❓ FAQ
            </Badge>
            <h2 className="text-4xl font-black text-slate-900 mb-4">
              Frequently Asked{' '}
              <span className="text-orange-600">Questions</span>
            </h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {[
              {
                q: "What is RIGHTNAME?",
                a: "RIGHTNAME is an AI-powered brand name evaluation tool that provides consulting-grade analysis of brand names. It checks trademark conflicts, domain availability, social media handles, and generates a comprehensive RightName Score report in under 90 seconds."
              },
              {
                q: "What does a RIGHTNAME report include?",
                a: "Each report includes: RightName Score (0-100), Trademark Risk Matrix with DuPont 13-Factor Analysis, Domain Availability Check, Social Handle Availability, Competitive Landscape Analysis, Cultural & Linguistic Analysis, Registration Timeline & Costs, Mitigation Strategies, and Alternative Name Suggestions."
              },
              {
                q: "How much does a report cost?",
                a: "We offer two pricing options: Single Report at $29 for validating one brand name, or the Founder's Pack at $49 for 3 reports - perfect for comparing your finalist names. The Founder's Pack saves you $38 compared to buying individually."
              },
              {
                q: "How long does it take to generate a report?",
                a: "Most reports are generated in 45-90 seconds. Our AI performs real-time trademark searches, domain checks, and comprehensive analysis to deliver consulting-grade insights quickly."
              },
              {
                q: "Is RIGHTNAME accurate for trademark checking?",
                a: "RIGHTNAME uses real-time web searches of trademark databases, phonetic similarity algorithms, DuPont 13-Factor likelihood of confusion analysis, and AI analysis to identify potential conflicts. While it provides comprehensive preliminary screening, we recommend consulting a trademark attorney for official legal advice before registration."
              },
              {
                q: "Which countries do you support?",
                a: "We support trademark analysis for 15+ countries including USA, India, UK, Canada, Australia, Germany, France, Japan, China, Brazil, UAE, Singapore, and more. Each report includes country-specific registration costs and timelines."
              },
              {
                q: "What is RightName Score and how is it calculated?",
                a: "RightName Score is our proprietary index (0-100) that evaluates brand names across 8 dimensions: Brand Distinctiveness, Cultural Resonance, Premiumisation Potential, Scalability, Trademark Safety, Consumer Perception, Digital Readiness, and Future-Proofing. Higher scores indicate stronger brand name candidates."
              },
              {
                q: "What is the difference between GO, CAUTION, and REJECT verdicts?",
                a: "GO means the name has low trademark risk and good availability. CAUTION indicates moderate risk - the name may work but needs careful consideration. REJECT means significant trademark conflicts exist and we recommend choosing a different name to avoid legal issues."
              },
              {
                q: "Can RIGHTNAME help with international brand naming?",
                a: "Yes! We analyze cultural and linguistic implications across multiple languages and regions. Our reports flag potential issues like negative meanings, pronunciation difficulties, or cultural sensitivities in your target markets."
              },
              {
                q: "What makes RIGHTNAME different from free trademark search tools?",
                a: "Free tools only check exact matches. RIGHTNAME provides comprehensive analysis including phonetic similarities, visual similarities, semantic conflicts, DuPont 13-Factor analysis, domain availability, social handle checks, competitive landscape mapping, and AI-powered risk assessment - similar to what a $50,000 brand consultancy delivers, for just $29-49."
              },
              {
                q: "Do I need to hire a trademark attorney after using RIGHTNAME?",
                a: "While RIGHTNAME provides thorough preliminary screening, we recommend consulting a trademark attorney before filing for trademark registration. Our reports help you identify strong candidates and potential issues, making your attorney consultation more efficient and cost-effective."
              }
            ].map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.q}
                answer={faq.a}
                isOpen={openFAQ === index}
                onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
              />
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-28 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-violet-600 via-fuchsia-500 to-orange-500 rounded-[2rem] blur-xl opacity-20"></div>
          <div className="relative bg-gradient-to-r from-violet-600 via-fuchsia-500 to-orange-500 rounded-[2rem] p-12 text-center text-white overflow-hidden">
            <div className="absolute top-4 left-4 text-6xl opacity-20">✨</div>
            <div className="absolute bottom-4 right-4 text-6xl opacity-20">🚀</div>
            <h2 className="text-3xl lg:text-4xl font-black mb-4">Ready to find your perfect name?</h2>
            <p className="text-lg text-white/80 mb-8 max-w-xl mx-auto">Join thousands of founders who have validated their brand names with RIGHTNAME.</p>
            <Button 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="bg-white text-violet-700 hover:bg-slate-100 font-black text-lg px-8 py-6 rounded-xl shadow-xl hover:scale-105 transition-transform"
            >
              Start Free Analysis <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>

      </div>
      
      {/* Brand Audit CTA Section - For Existing Brands */}
      <div className="py-20 px-6 bg-gradient-to-br from-emerald-50 via-white to-teal-50 border-t border-emerald-100">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-6">
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 font-bold">
                FOR EXISTING BRANDS
              </Badge>
              <h2 className="text-4xl md:text-5xl font-black text-slate-900">
                Already Have a <span className="text-emerald-600">Brand?</span>
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                Get a comprehensive audit of your existing brand with competitive analysis, 
                SWOT assessment, 8-dimension scoring, and strategic recommendations.
              </p>
              <div className="flex flex-wrap gap-3">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  Competitor Benchmarking
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  SWOT Analysis
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  Strategic Roadmap
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  8-Dimension Scoring
                </div>
              </div>
              <Button 
                onClick={() => navigate('/brand-audit')}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold text-lg px-8 py-6 rounded-xl shadow-xl shadow-emerald-300/50 hover:scale-105 transition-transform"
              >
                <BarChart3 className="w-5 h-5 mr-2" />
                Start Brand Audit
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <p className="text-sm text-emerald-600 font-semibold">100% FREE • No Credit Card Required</p>
            </div>
            
            {/* Right Content - Visual */}
            <div className="relative">
              <div className="bg-white rounded-2xl border-2 border-emerald-200 shadow-2xl shadow-emerald-200/50 p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-slate-900">Brand Audit Report</h3>
                  <Badge className="bg-emerald-100 text-emerald-700">Sample</Badge>
                </div>
                
                {/* Sample Radar Preview */}
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { name: "Heritage", score: 8 },
                    { name: "Satisfaction", score: 7 },
                    { name: "Positioning", score: 6 },
                    { name: "Growth", score: 9 },
                    { name: "Operations", score: 7 },
                    { name: "Awareness", score: 5 },
                    { name: "Viability", score: 8 },
                    { name: "Digital", score: 6 }
                  ].map((dim, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                      <span className="text-xs text-slate-600">{dim.name}</span>
                      <span className={`text-xs font-bold ${dim.score >= 7 ? 'text-emerald-600' : 'text-amber-600'}`}>{dim.score}/10</span>
                    </div>
                  ))}
                </div>
                
                {/* Sample Score */}
                <div className="text-center p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl">
                  <div className="text-3xl font-black text-emerald-600">72/100</div>
                  <div className="text-xs text-slate-500">Brand Health Score</div>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-20 h-20 bg-emerald-200 rounded-full blur-2xl opacity-60"></div>
              <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-teal-200 rounded-full blur-2xl opacity-60"></div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Case Studies / Sample Reports Section */}
      <CaseStudiesSection />
      
      {/* Pricing Section */}
      <div id="pricing" className="bg-gradient-to-br from-slate-50 via-white to-violet-50">
        <PricingSection brandNames={wizardPayload?.brand_names || []} />
      </div>
      
      {/* Footer */}
      <Footer />
      
      {/* Mismatch Warning Modal */}
      <MismatchWarningModal
        isOpen={showMismatchModal}
        onClose={() => setShowMismatchModal(false)}
        mismatches={detectedMismatches}
        formData={wizardPayload || {}}
        onContinue={handleMismatchContinue}
        onEdit={handleMismatchEdit}
        onApplyFix={(field, newValue) => {
          // Apply the fix - update wizard payload
          if (wizardPayload) {
            setWizardPayload({ ...wizardPayload, [field]: newValue });
          }
          setShowMismatchModal(false);
          setDetectedMismatches([]);
          toast.success(`Updated ${field.replace('_', ' ')} to "${newValue}"`);
        }}
      />
    </div>
    </>
  );
};

export default LandingPage;
